import { createSelector } from 'reselect'
import { getUnique, numSort } from '../../tools/Array'
import { getGroupFilter, getFavourites } from '../options/selectors'
import { FAVOURITES, isFavourite } from '../../tools/Favourites'
import { isOfficialRace } from '../../tools/Races'

/**
 * Get the highest lap id loaded
 * @param {Object} state redux state
 * @returns {number} lap id, 0 when nothing is loaded
 */
export const getLastId = ( state ) =>
{
  const { last_id = 0 } = state.data || {}
  return last_id
}

/**
 * Get the contest of the loaded laps
 * @param {Object} state redux state
 * @returns {Object|null} contest with id, description, date and day
 */
export const getDataContest = ( state ) =>
{
  const { contest = null } = state.data || {}
  return contest
}

/**
 * Get whether the loaded race is official, with groups; other races only have all cars and favourites
 * @param {Object} state redux state
 * @returns {boolean} whether the race is official
 */
export const getIsOfficialRace = createSelector(
  [ getDataContest ],
  ( contest ) => isOfficialRace( contest )
)

// get rows from state
const getAllRows = ( { data } ) => 
{
  // old style data where data contained the rows
  if ( Array.isArray( data ) )
  {
    console.warn( 'getAllRows(): old style data detected' )
    return data
  }
  
  // new style data where rows are in data.rows
  const { rows = [] } = data || {}
  return Array.isArray( rows ) ? rows : []
}

// get data
export const getData = createSelector(
  [ getAllRows ],
  ( data ) => data
)

// get unique groups
export const getGroups = createSelector(
  [ getAllRows ],
  ( data ) => getUnique( data, 'cat' )
)

// get unique car ids
export const getAllCars = createSelector(
  [ getAllRows ],
  ( data ) => getUnique( data, 'car' )
)

/**
 * Get the groups to choose from: the groups of the loaded laps of an official race, with the
 * favourites group last when one of the favourite cars has laps; other races only have the favourites
 * @param {Object} state redux state
 * @returns {Array<number|string>} groups
 */
export const getGroupList = createSelector(
  [ getGroups, getAllCars, getFavourites, getIsOfficialRace ],
  ( groups, cars, favourites, official ) =>
  {
    const shown = official ? groups : []
    return cars.some( car => isFavourite( car, favourites ) ) ? [ ...shown, FAVOURITES ] : shown
  }
)

// get unique car ids, take group into account
export const getGroupCars = createSelector(
  [ getAllRows, getGroupFilter ],
  ( data, group ) => getUnique( group ? data.filter( d => d.cat === group ) : data, 'car' )
)

// get unique car ids with groups
export const getCarsAndGroups = createSelector(
  [ getAllRows, getAllCars ],
  ( data, cars ) => {
    const out = {}
    for( const car of cars )
    {
      const dcar = data.find( d => d.car === car )
      if ( dcar ) 
      {
        // get car => group / category
        out[ car ] = dcar.cat
      }
    }
    return out
  }
)

// get unqiue car ids with details ( model & group )	
export const getCarsDetails = createSelector(
  [ getAllRows, getAllCars ],
  ( data, cars ) => {
    const out = {}
    for( const car of cars )
    {
      const dcar = data.find( d => d.car === car )
      if ( dcar ) 
      {
        // get number, category and model
        out[ car ] = {
          num: car,
          group: dcar.cat,
          model: dcar.model         
        }   
      }
    }
    return out
  }
)

// get unique penalties
export const getPenalties = createSelector(
  [ getAllRows ],
  ( data ) => getUnique( data, 'penalty', numSort )
)

// get fastest time of the day, returns the entire row
export const getFastestPerDay = createSelector(
  [ getAllRows ],
  ( data ) => {
    let fastest = null;
    for( const d of data )
    {
      if ( !fastest || d.laptime < fastest.laptime ) 
      {  
        if ( !d.penalty && !d.comment ) 
        {
          fastest = d
        }
      }
    }
    return fastest;
  }
)

// get fastest times per car
export const getFastestPerCar = createSelector(
  [ getAllRows ],
  ( data ) => {
    // strip invalid laptimes
    const valid_rows = data.filter( d => !d.penalty && !d.comment )
    // sort on laptime
    const sorted_rows = valid_rows.sort( ( a, b ) => a.laptime > b.laptime ? 1 : -1 )
    // cars for later use
    const cars = []
    // get fastest time per car
    const fast_rows = sorted_rows.filter( d => {
      if ( !cars.includes( d.car ) )
      {
        cars.push( d.car )
        return d
      }
      return null
    } )
    return fast_rows
  }
)

/**
 * Get the standings of each group: the fastest valid lap of each car, with its position in the group,
 * and all laps of the group; groups in the order of the group lists. A race that isn't official has
 * one group of all cars, with an empty key
 * @param {Object} state redux state
 * @returns {Array<{ group: number|string, standings: Array<Object>, laps: Array<Object> }>} groups
 */
export const getGroupStandings = createSelector(
  [ getGroups, getFastestPerCar, getAllRows, getIsOfficialRace ],
  ( groups, fastest, data, official ) =>
  {
    const standings = ( rows ) => rows.map( ( row, index ) => ( { ...row, pos: index + 1 } ) )
    if ( !official ) return data.length ? [ { group: '', standings: standings( fastest ), laps: data } ] : []

    return groups.map( group => (
      {
        group,
        standings: standings( fastest.filter( row => row.cat === group ) ),
        laps: data.filter( row => row.cat === group )
      }
    ) )
  }
)

// get fastest times per group, none when the race isn't official as it has no groups
export const getFastestPerGroup = createSelector(
  [ getAllRows, getIsOfficialRace ],
  ( data, official ) => {
    if ( !official ) return []
    // strip invalid laptimes
    const valid_rows = data.filter( d => !d.penalty && !d.comment )
    // sort on laptime
    const sorted_rows = valid_rows.sort( ( a, b ) => a.laptime > b.laptime ? 1 : -1 )
    // groups for later use
    const groups = []
    // get fastest time per car
    const fast_rows = sorted_rows.filter( d => {
      if ( !groups.includes( d.cat ) )
      {
        groups.push( d.cat )
        return d
      }
      return null
    } )
    
    return fast_rows
  }
)
